import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHeart, FaBed, FaBath, FaRuler, FaCar, FaCamera, FaPlay, FaBalanceScale } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import * as S from './styles';
import varandaImg from '../../assets/imoveis/01.webp';
import { useCurrency } from '../../contexts/CurrencyContext';

const PropertyCard = ({ property, onCompare, onViewPhotos, onViewVideo, onFavorite, onUnfavorite, isFavorited = false }) => {
  console.log('PropertyCard recebeu:', property);
  console.log('Chaves do property:', Object.keys(property || {}));
  const { fotos, tituloIMOVEL, enderecoIMOVEL, valorIMOVEL, quartosIMOVEL, banheirosIMOVEL, metragemIMOVEL, vagasgaragemIMOVEL, tag, tipometragemIMOVEL, descricaoIMOVEL, descricao, tipoIMOVEL, cidadeIMOVEL, cityIMOVEL, situacaoIMOVEL, estadoIMOVEL, exibirPreco } = property;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { currencySymbol, exchangeRate } = useCurrency();

  const formatPrice = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    const converted = num * exchangeRate;
    return `${currencySymbol} ${converted.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const areaUnit = (() => {
    const raw = String(tipometragemIMOVEL || '').toLowerCase();
    if (raw.includes('ft')) return 'ft²';
    if (raw.includes('m')) return 'm²';
    return 'm²';
  })();

  const slugify = (s) => {
    return (s || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');
  };

  const handleCardClick = () => {
    const code = property?.id || property?.codigoIMOVEL || property?.idIMOVEL;
    if (!code) return; // sem código, não navega
    const slug = property?.slugIMOVEL || property?.slug || slugify(tituloIMOVEL);
    const path = slug ? `/property/${code}/${slug}` : `/property/${code}`;
    navigate(path);
  };

  // Imagem principal: usa a foto vinda do backend.
  // Aceita formatos: array de strings (URL) ou array de objetos { urlFOTO, pathFOTO, padraoFOTO }
  const pickPrimary = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    // Se objetos, tenta a com padraoFOTO === 1
    const asObj = arr.find(x => x && typeof x === 'object');
    if (asObj) {
      const primary = arr.find(x => Number(x?.padraoFOTO) === 1) || arr[0];
      return primary?.urlFOTO || primary?.pathFOTO || null;
    }
    // Caso contrário, assume array de strings
    const asStr = typeof arr[0] === 'string' ? arr[0] : null;
    return asStr || null;
  };
  const firstPhotoUrl = pickPrimary(fotos) || varandaImg;

  // Tenta localizar o preço em vários campos possíveis
  const rawPrice =
    valorIMOVEL ??
    property?.precoIMOVEL ??
    property?.preco ??
    property?.valor ??
    property?.price ??
    null;
  const numericPrice = Number(rawPrice);
  const hasPrice = Number.isFinite(numericPrice) && numericPrice > 0;
  const shouldHidePrice = exibirPreco === false || exibirPreco === 0 || exibirPreco === '0';
  const priceText = shouldHidePrice ? 'Sob consulta' : (hasPrice ? formatPrice(numericPrice) : 'Sob consulta');

  // Gera resumo inteligente do imóvel
  const generatePropertySummary = () => {
    const desc = descricaoIMOVEL || descricao || '';
    
    // Se tiver descrição, mostra as primeiras 2-3 linhas
    if (desc && desc.trim()) {
      const lines = desc.split('\n').filter(line => line.trim());
      const previewLines = lines.slice(0, 3);
      const preview = previewLines.join(' ').trim();
      if (preview.length > 150) {
        return preview.substring(0, 150) + '...';
      }
      return preview + (lines.length > 3 ? '...' : '');
    }

    // Se não tiver descrição, monta resumo com atributos disponíveis
    const summaryParts = [];
    
    // Tipo do imóvel
    const tipo = tipoIMOVEL;
    if (tipo) {
      const tipoLabels = { 
        1: t('propertyCard.category.buy'), 
        2: t('propertyCard.category.rent'), 
        3: t('propertyCard.category.vacation') 
      };
      summaryParts.push(tipoLabels[tipo] || `Tipo ${tipo}`);
    }

    // Cidade
    const cidade = cidadeIMOVEL || cityIMOVEL;
    if (cidade) {
      summaryParts.push(cidade);
    }

    // Estado de conservação
    const estado = situacaoIMOVEL || estadoIMOVEL;
    if (estado) {
      summaryParts.push(estado);
    }

    // Características principais
    const features = [];
    if (quartosIMOVEL && quartosIMOVEL !== '-') {
      features.push(`${quartosIMOVEL} ${Number(quartosIMOVEL) === 1 ? t('propertyCard.bedrooms').slice(0, -1) : t('propertyCard.bedrooms')}`);
    }
    if (metragemIMOVEL && metragemIMOVEL !== '-') {
      features.push(`${metragemIMOVEL}m²`);
    }
    if (features.length > 0) {
      summaryParts.push(features.join(' • '));
    }

    return summaryParts.length > 0 ? summaryParts.join(' • ') : 'Imóvel com características variadas';
  };

  const propertySummary = generatePropertySummary();

  return (
    <S.Card onClick={handleCardClick}>
      <S.ImageContainer>
        <S.Image src={firstPhotoUrl} alt={tituloIMOVEL || 'Sem título'} />
        {tag && <S.Tag>{tag}</S.Tag>}
        <S.HeartButton
          $active={isFavorited}
          onClick={e => {
            e.stopPropagation();
            if (isFavorited) {
              if (onUnfavorite) onUnfavorite(property);
            } else {
              if (onFavorite) onFavorite(property);
            }
          }}
          title={isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
        >
          <FaHeart />
        </S.HeartButton>
        <S.HoverActions>
          <button onClick={(e) => { e.stopPropagation(); if (onViewPhotos) onViewPhotos(property); }} title="Ver fotos">
            <FaCamera />
          </button>
          <button onClick={(e) => { e.stopPropagation(); if (onViewVideo) onViewVideo(property); }} title="Ver vídeo">
            <FaPlay />
          </button>
          <button onClick={(e) => { e.stopPropagation(); if (onCompare) onCompare(property); }} title="Comparar imóvel">
            <FaBalanceScale />
          </button>
        </S.HoverActions>
      </S.ImageContainer>
      
      <S.Content>
        <S.Title>{tituloIMOVEL || 'Título não disponível'}</S.Title>
        <S.Location>{enderecoIMOVEL || 'Localização não disponível'}</S.Location>
        
        <S.Summary>{propertySummary}</S.Summary>
        
        <S.Details>
          {quartosIMOVEL && quartosIMOVEL !== '-' && (
            <S.DetailItem>
              <FaBed />
              {quartosIMOVEL} {t('propertyCard.bedrooms')}
            </S.DetailItem>
          )}
          {banheirosIMOVEL && banheirosIMOVEL !== '-' && (
            <S.DetailItem>
              <FaBath />
              {banheirosIMOVEL} {Number(banheirosIMOVEL) === 1 ? t('propertyCard.bathrooms.singular') : t('propertyCard.bathrooms.plural')}
            </S.DetailItem>
          )}
          {metragemIMOVEL && metragemIMOVEL !== '-' && (
            <S.DetailItem>
              <FaRuler />
              {metragemIMOVEL}{areaUnit}
            </S.DetailItem>
          )}
          {vagasgaragemIMOVEL && vagasgaragemIMOVEL !== '-' && (
            <S.DetailItem>
              <FaCar />
              {vagasgaragemIMOVEL} {Number(vagasgaragemIMOVEL) === 1 ? t('propertyCard.parking.singular') : t('propertyCard.parking.plural')}
            </S.DetailItem>
          )}
        </S.Details>
        
        <S.Price>
          {shouldHidePrice ? (
            <>
              <div style={{ whiteSpace: 'nowrap' }}>Sob consulta</div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Entre em contato com o proprietário para mais informações sobre o valor deste imóvel.
              </div>
            </>
          ) : (
            <>
              {priceText} {(() => {
                if (!hasPrice) return null;
                const type = property?.tipoIMOVEL ||
                         property?.tipo ||
                         property?.categoria?.tipo?.codigoTipo ||
                         property?.categoria?.tipo?.codigoTIPOIMOVEL ||
                         property?.categoria?.tipo;
                const label = String(property?.labelvalorIMOVEL || property?.labelvalor || property?.labelValor || '').toLowerCase();

                // Se o label explicitar algo, prioriza (ex: ferias, temporada, mensal)
                if (label.includes('feri') || label.includes('temporada') || label.includes('vacation')) return <span> / noite</span>;
                if (label.includes('mes') || label.includes('mês') || label.includes('month')) return <span> / mês</span>;

                // Caso contrário, usa o código do tipo
                if (Number(type) === 1) return null; // Venda
                if (Number(type) === 2) return <span> / mês</span>; // Arrendamento
                if (Number(type) === 3) return <span> / noite</span>; // Férias

                // Fallback: se não soubermos, mas for valor baixo, pode ser aluguel, mas por segurança não mostramos /noite em tudo
                // No entanto, na home do JoviPlanet, o padrão costumava ser /noite.
                // Mas o usuário reclamou que em "Venda" aparece /noite.
                // Então, se for tipo 1 (venda), garantimos que NÃO aparece.
                return null;
              })()}
            </>
          )}
        </S.Price>
      </S.Content>
    </S.Card>
  );
};

export default PropertyCard;